import { queryClient } from "../../query-client";
import { MapMarker } from "../../components/map/map.types";
import { UniqueIdentifier } from "@dnd-kit/core";
import {
  createLocationsQueryKey,
  createRegionsQueryKey,
} from "service/api/estabrookQueryKeys";
import {
  AdminRegionLocationResDto,
  AdminRegionResDto,
} from "service/api/estabrookSchemas";
import {
  AdminLocationV2ControllerGetAllLocationsResponse,
  AdminRegionV2ControllerGetRegionsResponse,
  AdminRegionV2ControllerTransferLocationsToRegionResponse,
} from "service";

export const findContainer = (
  id: UniqueIdentifier,
  data: AdminRegionResDto[],
) => {
  if (!id) return null;
  const idStr = id.toString();

  // Find the route containing this location id
  const region = data.find((region) =>
    region.locations.some((location) => location.locationId === idStr),
  );

  if (!region) return null;

  // Return container id in format: route-id-status
  return region.id;
};

export const getCurrentRegionsAndItems = (
  activeContainer: string,
  overContainer: string,
  activeItemId: UniqueIdentifier,
  regions: AdminRegionResDto[],
) => {
  const currentRegions = [...regions];
  const activeRegionItems = regions.find(
    (route) => route.id === activeContainer,
  )?.locations;
  const overRegionItems = regions.find(
    (route) => route.id === overContainer,
  )?.locations;
  const currentItem = activeRegionItems?.find(
    (item) => item.locationId === activeItemId,
  );
  const activeIndex = activeRegionItems?.findIndex(
    (item) => item.locationId === activeItemId,
  );
  const overIndex = overRegionItems?.findIndex(
    (item) => item.locationId === activeItemId,
  );

  return {
    currentRegions,
    currentItem,
    activeIndex,
    overIndex,
    activeRegionItems,
  };
};

export const removeSelectedAddressesOnDrag = (
  currentRegions: AdminRegionResDto[],
  currentMovingItem: AdminRegionLocationResDto,
  selectedAddresses: MapMarker[],
  shouldFilterSelected: boolean,
  setShouldFilterSelected: (value: boolean) => void,
): AdminRegionResDto[] => {
  const selectedItemsToMove: AdminRegionLocationResDto[] = currentRegions
    .flatMap((region) => region.locations)
    .filter((location) =>
      selectedAddresses.some(
        (marker) =>
          marker.id === location.locationId &&
          marker.id !== currentMovingItem.locationId,
      ),
    );

  if (selectedItemsToMove.length === 0 || !shouldFilterSelected)
    return currentRegions;

  setShouldFilterSelected(false);

  const updatedRoutes = currentRegions.map((route) => {
    return {
      ...route,
      locations: route.locations.filter(
        (location) =>
          !selectedItemsToMove.some(
            (item) => item.locationId === location.locationId,
          ),
      ),
    };
  });

  return updatedRoutes;
};

export const updateRegionsCache = (
  data: AdminRegionV2ControllerTransferLocationsToRegionResponse,
) => {
  // Optimistically update the regions query cache with the new data
  // Match the query key used by useRegions hook (limit: 100, page: 1)
  const regionsQueryKey = createRegionsQueryKey(100, 1);

  queryClient.setQueryData(
    regionsQueryKey,
    (oldData: AdminRegionV2ControllerGetRegionsResponse | undefined) => {
      if (!oldData || !data?.data) return oldData;

      // The API response contains both the source region and target region
      const sourceRegion = data.data.region;
      const targetRegion = data.data.targetRegion;

      const updatedRegions = oldData.data?.map((region: AdminRegionResDto) => {
        // Update source region with data from API response
        if (region.id === sourceRegion?.id) {
          return sourceRegion;
        }

        // Update target region with data from API response
        if (region.id === targetRegion?.id) {
          return targetRegion;
        }

        return region;
      });

      return {
        ...oldData,
        data: updatedRegions,
      };
    },
  );
};

export const updateLocationsCache = (
  data: AdminRegionV2ControllerTransferLocationsToRegionResponse,
  flatRate: boolean,
  onDemand: boolean,
  unassignedOrders: boolean,
) => {
  // Also update the locations query cache with the new sequence numbers
  // Generate query params based on current filter states
  const getLocationQueryParams = () => {
    const params: Record<string, string | string[] | boolean> = {};

    // Handle type filter (flat rate = "annual", on demand = "onDemand")
    const selectedTypes: string[] = [];
    if (flatRate) selectedTypes.push("annual");
    if (onDemand) selectedTypes.push("onDemand");

    if (selectedTypes.length > 0) {
      params.type = selectedTypes;
    }

    // Handle assigned filter (unassigned orders = assigned: false)
    if (unassignedOrders) {
      params.assigned = false;
    }

    return params;
  };

  const locationsQueryKey = createLocationsQueryKey(getLocationQueryParams());

  queryClient.setQueryData(
    locationsQueryKey,
    (
      oldLocationsData:
        | AdminLocationV2ControllerGetAllLocationsResponse
        | undefined,
    ) => {
      if (!oldLocationsData || !data?.data) return oldLocationsData;

      const sourceRegion = data.data.region;
      const targetRegion = data.data.targetRegion;

      // Create separate maps for source and target region locations
      const sourceLocationMap = new Map<string, number>();
      const targetLocationMap = new Map<string, number>();

      // Add locations from source region
      sourceRegion?.locations?.forEach((location) => {
        sourceLocationMap.set(location.locationId, location.locationSequence);
      });

      // Add locations from target region
      targetRegion?.locations?.forEach((location) => {
        targetLocationMap.set(location.locationId, location.locationSequence);
      });

      // Update the locations data with new sequence numbers and correct region info
      const updatedLocations = oldLocationsData.data?.map((location) => {
        // Check if location is in target region (moved locations)
        if (targetLocationMap.has(location.id) && targetRegion) {
          return {
            ...location,
            region: {
              id: targetRegion.id,
              name: targetRegion.name,
              color: targetRegion.color,
            },
            locationSequence: targetLocationMap.get(location.id),
          };
        }

        // Check if location is in source region (locations that stayed but had sequence updates)
        if (sourceLocationMap.has(location.id) && sourceRegion) {
          return {
            ...location,
            region: {
              id: sourceRegion.id,
              name: sourceRegion.name,
              color: sourceRegion.color,
            },
            locationSequence: sourceLocationMap.get(location.id),
          };
        }

        return location;
      });

      return {
        ...oldLocationsData,
        data: updatedLocations,
      };
    },
  );
};

/**
 * Optimistically updates regions state after a drag operation
 * to reflect the final position before API call
 */
export const updateRegionsOptimistically = (
  regions: AdminRegionResDto[],
  locationsToMove: string[],
  sourceRegionId: string,
  targetRegionId: string,
  targetSequence: number,
): AdminRegionResDto[] => {
  // Get the locations that need to be moved
  const sourceRegion = regions.find((region) => region.id === sourceRegionId);
  const targetRegion = regions.find((region) => region.id === targetRegionId);

  if (!sourceRegion) return regions;

  // Handle same region reordering
  if (sourceRegionId === targetRegionId) {
    return regions.map((region) => {
      if (region.id === sourceRegionId) {
        // Get the locations that will be moved
        const locationsBeingMoved = region.locations.filter((location) =>
          locationsToMove.includes(location.locationId),
        );

        // Get locations that are not being moved, sorted by sequence
        const remainingLocations = region.locations
          .filter((location) => !locationsToMove.includes(location.locationId))
          .sort(
            (a, b) => (a.locationSequence || 0) - (b.locationSequence || 0),
          );

        // Convert targetSequence to array index (targetSequence is 1-based, array is 0-based)
        const targetIndex = Math.max(0, targetSequence - 1);

        // Create a new array by inserting moved items at the target position
        const newOrderedLocations = [...remainingLocations];

        // Insert moved locations at the target index
        locationsBeingMoved.forEach((location, index) => {
          newOrderedLocations.splice(targetIndex + index, 0, location);
        });

        // Resequence all locations based on their new positions
        const resequencedLocations = newOrderedLocations.map(
          (location, index) => ({
            ...location,
            locationSequence: index + 1,
          }),
        );

        return {
          ...region,
          locations: resequencedLocations,
        };
      }
      return region;
    });
  }

  // Handle cross-region moves
  if (!targetRegion) return regions;

  // Get the locations that will be moved
  const locationsBeingMoved = sourceRegion.locations.filter((location) =>
    locationsToMove.includes(location.locationId),
  );

  // Update the regions
  return regions.map((region) => {
    if (region.id === sourceRegionId) {
      // Remove moved locations from source region
      const remainingLocations = region.locations.filter(
        (location) => !locationsToMove.includes(location.locationId),
      );

      // Resequence remaining locations (close gaps)
      const resequencedLocations = remainingLocations
        .sort((a, b) => (a.locationSequence || 0) - (b.locationSequence || 0))
        .map((location, index) => ({
          ...location,
          locationSequence: index + 1,
        }));

      return {
        ...region,
        locations: resequencedLocations,
      };
    }

    if (region.id === targetRegionId) {
      // Get existing locations (excluding any moved items that might already be there)
      const existingLocations = region.locations
        .filter((location) => !locationsToMove.includes(location.locationId))
        .sort((a, b) => (a.locationSequence || 0) - (b.locationSequence || 0));

      // Convert targetSequence to array index (targetSequence is 1-based, array is 0-based)
      const targetIndex = Math.max(0, targetSequence - 1);

      // Create a new array by inserting moved items at the target position
      const newOrderedLocations = [...existingLocations];

      // Insert moved locations at the target index
      locationsBeingMoved.forEach((location, index) => {
        newOrderedLocations.splice(targetIndex + index, 0, location);
      });

      // Resequence all locations based on their new positions
      const resequencedLocations = newOrderedLocations.map(
        (location, index) => ({
          ...location,
          locationSequence: index + 1,
        }),
      );

      return {
        ...region,
        locations: resequencedLocations,
      };
    }

    return region;
  });
};
